import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageSquare, Sparkles, X } from "lucide-react";

const GuideCtx = createContext({ say: () => { }, tried: 0 });

export const useGuide = () => useContext(GuideCtx);

/** The little voice that narrates the playground and slips in the pitch. */
export function GuideProvider({ children }) {
    const [msg, setMsg] = useState({ text: "Scroll. Touch everything. Nothing here leaves the page — this whole thing is a demo you can break.", id: 0 });
    const [tried, setTried] = useState(0);
    const [hidden, setHidden] = useState(false);
    const seen = useRef(new Set());

    const say = useCallback((text, key) => {
        setMsg({ text, id: Date.now() });
        const k = key || text;
        if (!seen.current.has(k)) {
            seen.current.add(k);
            setTried((n) => n + 1);
        }
    }, []);

    return (
        <GuideCtx.Provider value={{ say, tried }}>
            {children}
            <AnimatePresence>
                {!hidden && (
                    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 30 }}
                        className="fixed bottom-4 left-4 z-[60] w-[min(92vw,25rem)]" data-testid="guide-bubble">
                        <div className="dark-block relative overflow-hidden rounded-3xl border border-brand-green/35 bg-black/85 p-4 pr-10 backdrop-blur-xl">
                            <button onClick={() => setHidden(true)} className="absolute right-3 top-3 text-zinc-500 hover:text-white" data-testid="guide-hide"><X size={15} /></button>
                            <div className="flex items-center gap-2">
                                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-green text-black"><MessageSquare size={13} /></span>
                                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand-green">13thav guide</p>
                                <span className="ml-auto font-mono text-[11px] uppercase tracking-widest text-zinc-500" data-testid="guide-tried">{tried} tried</span>
                            </div>
                            <motion.p key={msg.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                                className="mt-2.5 text-[13.5px] leading-relaxed text-zinc-100" data-testid="guide-text">
                                {msg.text}
                            </motion.p>
                            <a href="#quote" className="mt-3 inline-flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-brand-green hover:underline" data-testid="guide-cta">
                                <Sparkles size={12} /> Build mine in 7 days
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </GuideCtx.Provider>
    );
}

/** Frame around every micro-demo — picks up the accent of the chapter it sits in. */
export function DemoCard({ title, hint, children, span = "", testid }) {
    return (
        <div className={`group/demo flex flex-col overflow-hidden rounded-3xl border border-white/10 bg-[color:var(--chapter-tint,rgba(255,255,255,0.025))] p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-[color:var(--chapter-accent,#2563EB)] hover:shadow-[0_18px_40px_-24px_var(--chapter-accent,#2563EB)] ${span}`}
            data-testid={testid}>
            <div className="flex items-baseline justify-between gap-3">
                <h3 className="font-display text-base font-bold leading-tight">{title}</h3>
                {hint && <span className="shrink-0 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500 transition-colors group-hover/demo:text-[color:var(--chapter-accent,#2563EB)]">{hint}</span>}
            </div>
            <div className="mt-4 flex-1">{children}</div>
        </div>
    );
}

const TEXTURES = {
    dots: "radial-gradient(currentColor 1px, transparent 1px)",
    grid: "linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)",
    diagonal: "repeating-linear-gradient(45deg, currentColor 0 1px, transparent 1px 14px)",
};

/** Chapter intro screen you scroll into, then the grid — each chapter has its own identity. */
export function Chapter({ n, title, blurb, statement, accent = "#2563EB", texture = "dots", children, id }) {
    return (
        <section id={id} style={{ "--chapter-accent": accent, "--chapter-tint": `${accent}0d` }} data-testid={`chapter-${n}`}>
            <div className="relative overflow-hidden border-y border-white/10">
                <div className="pointer-events-none absolute inset-0 opacity-[0.07]" style={{ color: accent, backgroundImage: TEXTURES[texture], backgroundSize: texture === "diagonal" ? "auto" : "22px 22px" }} />
                <div className="pointer-events-none absolute -left-32 top-0 h-72 w-72 rounded-full blur-[110px]" style={{ background: accent, opacity: 0.14 }} />
                <div className="relative mx-auto flex max-w-7xl flex-col gap-6 px-4 py-16 sm:px-6 lg:flex-row lg:items-end lg:justify-between lg:py-20">
                    <motion.div initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.55 }}>
                        <p className="font-mono text-[11px] uppercase tracking-[0.4em]" style={{ color: accent }}>chapter {String(n).padStart(2, "0")}</p>
                        <h2 className="mt-4 font-display text-4xl font-black leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl">{title}</h2>
                        <p className="mt-4 max-w-2xl text-base text-zinc-300 md:text-lg">{blurb}</p>
                    </motion.div>
                    {statement && (
                        <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ delay: 0.15 }}
                            className="max-w-sm border-l-2 pl-5 font-display text-lg font-bold leading-snug text-white" style={{ borderColor: accent }}>
                            {statement}
                        </motion.p>
                    )}
                </div>
            </div>
            <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
                <div className="grid items-start gap-5 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
            </div>
        </section>
    );
}

/** Thin progress bar that tracks how far down the playground you are. */
export function ScrollProgress() {
    const [pct, setPct] = useState(0);
    useEffect(() => {
        const on = () => {
            const h = document.documentElement;
            setPct((h.scrollTop / (h.scrollHeight - h.clientHeight)) * 100);
        };
        window.addEventListener("scroll", on, { passive: true });
        on();
        return () => window.removeEventListener("scroll", on);
    }, []);
    return (
        <div className="fixed left-0 top-0 z-[70] h-0.5 w-full bg-transparent" data-testid="scroll-progress">
            <div className="h-full bg-brand-green transition-[width] duration-150" style={{ width: `${pct}%` }} />
        </div>
    );
}

/** Green sparks that follow the cursor across the whole playground. */
export function CursorTrail() {
    const [dots, setDots] = useState([]);
    useEffect(() => {
        let last = 0;
        const on = (e) => {
            const now = Date.now();
            if (now - last < 45) return;
            last = now;
            setDots((d) => [...d.slice(-14), { id: now, x: e.clientX, y: e.clientY }]);
        };
        window.addEventListener("pointermove", on);
        return () => window.removeEventListener("pointermove", on);
    }, []);
    return (
        <div className="pointer-events-none fixed inset-0 z-[55] hidden lg:block" data-testid="cursor-trail">
            {dots.map((d, i) => (
                <span key={d.id} className="absolute rounded-full bg-brand-green"
                    style={{ left: d.x, top: d.y, width: 6 - i * 0.3, height: 6 - i * 0.3, opacity: (i + 1) / dots.length * 0.5, transform: "translate(-50%,-50%)" }} />
            ))}
        </div>
    );
}
